/**
 * Shared Job Validation Module
 *
 * This module provides centralized validation logic for job imports across both:
 * - ImportTable.tsx (bulk import with programmatic validation)
 * - SingleImportForm.tsx (single entry with react-hook-form)
 *
 * All validation rules, error messages, and business logic are consolidated in this module
 * family to ensure consistency across the application:
 * - jobValidation.ts              — value validators + batch validation (this file)
 * - jobValidation.requirements.ts — per-field required/visible rules (isFieldRequired, isFieldVisible)
 * - jobValidation.fieldKinds.ts   — field-kind classification + isEmpty
 * - jobValidation.messages.ts     — ValidationMessages (re-exported below)
 */

import type { Deal } from "@domain/CRM/deal";
import { geometryHasCoordinates } from "@domain/Geo/geometry";

import type { Job } from "../types/Job";
import { ImportTableColumn } from "@domain/Settings";
import { CREATE_NEW_SALESPERSON } from "@domain/constants";
import { isValidPhoneNumber } from "libphonenumber-js/min";
import type { ValidationContext, ValidationResult } from "../types";
import { columnDisplayNames } from "../types";
import { isEmpty, isBooleanField } from "./jobValidation.fieldKinds";
import { isFieldRequired, isFieldVisible } from "./jobValidation.requirements";

// #region DEFINITIONS

const regex = {
	positiveInteger: /^[0-9][0-9]*$/, // Positive integers only (>0)
	positiveDecimal: /^(?!(?:0+)(?:\.0+)?$)\d+(\.\d+)?$/, // Positive decimals only (>0), disallows 0 or 0.0; allows 0.1, 05.5, 5, 5.123
	email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// Use Map for O(1) regex pattern lookup with explicit typing
const NUMERIC_PATTERNS = new Map<ImportTableColumn, RegExp>([
	["frequencyOfMicros", regex.positiveInteger],
	["depth", regex.positiveDecimal],
	["density", regex.positiveDecimal],
]);

// The message table lives in a sibling module (this file is at its max-lines cap) and is
// re-exported here so `ValidationMessages` keeps its documented import path.
import { ValidationMessages } from "./jobValidation.messages";
export { ValidationMessages };

/**
 * A single validation problem on one field of one job.
 *
 * `blocking` is `isFieldVisible(field, jobContext)` — only blocking issues
 * gate submission. Errors on fields the user can't see/edit (hidden by company
 * settings, not required) are still surfaced for display but never block.
 */
export interface JobIssue {
	field: ImportTableColumn;
	message: string;
	kind: "missing" | "invalid";
	blocking: boolean;
}

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
type FieldProperty = keyof Job | keyof Job["files"] | "salespersonMobilePhone";
export const FIELD_TO_PROPERTY_MAP: Record<ImportTableColumn, FieldProperty> = {
	// Basic Information
	grower: "importGrowerName",
	farm: "importFarmName",
	field: "importFieldName",

	// Attachments
	boundaryGeoJSON: "boundaryGeoJSON",
	pointsGeoJSON: "pointsGeoJSON",
	zonesGeoJSON: "zonesGeoJSON",
	fieldNotes: "fieldNotes",

	// Order
	isReadyToSample: "isReadyToSample",
	dateFieldReadyEstimated: "dateFieldReadyEstimated",
	fieldPriority: "fieldPriority",
	collectBoundary: "collectBoundary",
	submissionNotes: "submissionNotes",

	// Deal
	density: "importDensity",
	depth: "importDepth",
	labName: "labName",
	testPackage: "externalTestPackageId",
	frequencyOfMicros: "externalFrequencyOfMicros",
	deal: "dealIds",

	// Lab Info
	labInstructions: "labInstructions",
	microsList: "microsList",
	lastCrop: "lastCrop",
	nextCrop: "nextCrop",
	yieldGoal: "yieldGoal",

	// Contacts
	branch: "branchIds",
	salesperson: "salespersonIds",
	salespersonMobilePhone: "salespersonMobilePhone",
	growerPhoneNumber: "growerPhoneNumber",
	submitterName: "submitterName",
	submitterEmail: "submitterEmail",

	// Sync Codes
	eventId: "eventId",
	labSubmissionCode: "labSubmissionCode",
	fieldId: "fieldId",
	externalId: "externalId",
	externalFarmId: "externalFarmId",
	externalLabId: "externalLabId",

	// Misc
	isTestJob: "isTestJob",
};

// #endregion

// #region VALIDATION FUNCTIONS

/**
 * Validates a single field value based on its type and context
 * This is the main validation function used by both components
 */
export function validateField(fieldType: ImportTableColumn, value: any, context: ValidationContext): ValidationResult {
	// Boolean fields are always valid - they have only two states (true/false), both valid
	if (isBooleanField(fieldType)) {
		return { isValid: true };
	}

	const isRequired = isFieldRequired(fieldType, context);
	const valueIsEmpty = isEmpty(value, fieldType);

	// Handle empty values: valid if not required, error if required
	if (valueIsEmpty) {
		return isRequired
			? {
					isValid: false,
					error: ValidationMessages[fieldType]?.required || `${columnDisplayNames[fieldType]} is required`,
				}
			: { isValid: true };
	}

	// Field-specific validation
	switch (fieldType) {
		case "frequencyOfMicros":
		case "depth":
		case "density":
			return validateNumeric(value, fieldType);

		case "growerPhoneNumber":
		case "salespersonMobilePhone":
			return validatePhone(value, context, fieldType);

		case "submitterEmail":
			return validateEmail(value, fieldType);

		case "salesperson":
			return validateSalesperson(value, context.job);

		default:
			// For all other fields, if they're required and we got here,
			// the isEmpty check above handles validation
			return { isValid: true };
	}
}

/** Validate numeric fields (density, depth, frequencyOfMicros) */
function validateNumeric(value: any, fieldType: ImportTableColumn): ValidationResult {
	const pattern = NUMERIC_PATTERNS.get(fieldType) ?? /.*/;
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid number";

	// Check for empty values (but allow 0)
	if (value === undefined || value === null || value === "") {
		return { isValid: false, error: invalidMessage };
	}

	// Convert to number and check if valid
	const numValue = Number(value);
	if (isNaN(numValue)) {
		return { isValid: false, error: invalidMessage };
	}

	// Check pattern (validates positive numbers, decimals, etc.)
	const stringValue = value.toString();
	if (!pattern.test(stringValue)) {
		return { isValid: false, error: invalidMessage };
	}

	return { isValid: true };
}

/** Validate phone numbers */
function validatePhone(
	value: any,
	context: ValidationContext,
	fieldType: "growerPhoneNumber" | "salespersonMobilePhone",
): ValidationResult {
	const isRequired = isFieldRequired(fieldType, context);
	const requiredMessage = ValidationMessages[fieldType].required || "Phone number is required";
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid phone number";

	if (!isRequired && isEmpty(value, fieldType)) {
		return { isValid: true };
	}

	if (isRequired && isEmpty(value, fieldType)) {
		return {
			isValid: false,
			error: requiredMessage,
		};
	}

	// If we have a value, validate the phone format
	if (value) {
		const phoneString = value.toString();
		const valid = isValidPhoneNumber(phoneString, "US");

		if (!valid) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Pure email-format check. Shared by validation and import job seeding. */
export function isValidEmail(value: string): boolean {
	return regex.email.test(value);
}

/** Validate email addresses */
function validateEmail(value: any, fieldType: ImportTableColumn): ValidationResult {
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid email address";

	if (value && typeof value === "string") {
		if (!isValidEmail(value)) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Validate salesperson (including new salesperson creation) */
function validateSalesperson(_value: any, job?: Job): ValidationResult {
	// Note: Empty array check is handled by isEmpty() in validateField()
	// This function only validates the content when a value is present

	// Check for new salesperson validation
	if (job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
		if (!job.newSalesperson?.name || job.newSalesperson.name.trim() === "") {
			return { isValid: false, error: "New salesperson name is required" };
		}
	}

	return { isValid: true };
}

const FileTypes = ["boundaryGeoJSON", "pointsGeoJSON", "zonesGeoJSON", "fieldNotes"] as const;
export type FileType = (typeof FileTypes)[number];
const FILE_TYPES_SET = new Set<string>(FileTypes);

// Pre-computed field types without files for validateJobs (avoids Object.keys() allocation per iteration)
const FIELD_TYPES_WITHOUT_FILES = (Object.keys(FIELD_TO_PROPERTY_MAP) as ImportTableColumn[]).filter(
	(ft) => !FILE_TYPES_SET.has(ft),
);

/** Check if a job has *usable* GeoJSON data for a given file type.
 *
 * Geometry-aware on purpose: an integration can hand back a feature whose
 * geometry has empty coordinates (Agrian's empty boundary, the 2026-08-06
 * outage). Counting that as "file present" let a boundary-less job sail past
 * the required-file validation — a job with only empty geometry has no file. */
function hasGeoJSON(job: Job, fileType: FileType): boolean {
	if (fileType === "fieldNotes") return false;
	const geojson = job.files?.[fileType] as GeoJSON.FeatureCollection | undefined;
	return (
		!!geojson &&
		Array.isArray(geojson.features) &&
		geojson.features.some((feature) => geometryHasCoordinates(feature.geometry))
	);
}

/** Validate file upload fields */
export function validateFile(fileType: FileType, job: Job, context: ValidationContext): ValidationResult {
	const isRequired = isFieldRequired(fileType as ImportTableColumn, context);

	if (!isRequired) {
		return { isValid: true };
	}

	if (!hasGeoJSON(job, fileType)) {
		return {
			isValid: false,
			error:
				ValidationMessages[fileType as ImportTableColumn]?.required || `${columnDisplayNames[fileType]} is required`,
		};
	}

	return { isValid: true };
}

// #endregion

// #region BATCH VALIDATION (for ImportTable)

/**
 * Creates a standardized validation error key
 * @param fieldType - The field/column identifier
 * @param jobId - The job ID
 * @returns Error key in format: `${fieldType}-${jobId}`
 *
 * @example
 * getValidationErrorKey("density", "job-123") // "density-job-123"
 * getValidationErrorKey("grower", "job-456") // "grower-job-456"
 */
export function getValidationErrorKey(fieldType: ImportTableColumn, jobId: string): string {
	return `${fieldType}-${jobId}`;
}

/**
 * Parses a validation error key to extract the field type
 * @param errorKey - The error key in format `${fieldType}-${jobId}`
 * @returns The field type, or undefined if the key is malformed
 *
 * @example
 * parseValidationErrorKey("density-job-123") // "density"
 * parseValidationErrorKey("grower-job-456") // "grower"
 */
export function parseValidationErrorKey(errorKey: string): ImportTableColumn | undefined {
	if (!errorKey.includes("-")) {
		return undefined;
	}
	const parts = errorKey.split("-");
	if (parts.length < 2) {
		return undefined;
	}
	return parts[0] as ImportTableColumn;
}

/** Pre-compute the deal lookup map once for O(1) per-job access. */
function buildDealMap(context: ValidationContext): Map<string, Deal> {
	const dealMap = new Map<string, Deal>();
	if (context.allDeals) {
		for (const deal of context.allDeals) {
			dealMap.set(deal.id, deal);
		}
	}
	return dealMap;
}

/**
 * Assemble the per-job validation context (deal lookup + derived flags).
 * `isPointsAttached` is included for all fields — only the pointsGeoJSON
 * requirement reads it, so this is harmless for non-file fields and lets the
 * file and non-file loops share one context.
 */
function buildJobContext(job: Job, context: ValidationContext, dealMap: Map<string, Deal>): ValidationContext {
	const isCreatingNewSalesperson = job.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;
	const dealId = job.dealIds?.[0];
	const deal: Deal | undefined = dealId ? dealMap.get(dealId) : undefined;
	return {
		...context,
		job,
		deal,
		isCreatingNewSalesperson,
		isPointsAttached: job.isPointsAttached,
		// The caller's context may already have a memoized _syncMethodCache (e.g. from a
		// prior syncMethodContains call against a different deal). Since `deal` above can
		// differ from context.deal, inheriting that cache would silently answer sync-method
		// questions against the wrong deal — invalidate it so it recomputes from this job's deal.
		_syncMethodCache: undefined,
	};
}

/**
 * Validate multiple jobs into structured per-job issues — the single source of
 * truth for validation. Each issue carries `blocking` (= isFieldVisible for the
 * field, evaluated with the same context validateField/validateFile use), so
 * gates can ignore errors on fields the user can't see/edit while displays can
 * still surface them. `validateJobs` (flat error map) and `classifyJobsValidity`
 * are both derived from this so the card, the NEXT gate, and the modal agree.
 */
export function validateJobsToIssues(jobs: Job[], context: ValidationContext): Record<string, JobIssue[]> {
	const dealMap = buildDealMap(context);
	const issuesByJob: Record<string, JobIssue[]> = {};

	for (const job of jobs) {
		const issues: JobIssue[] = [];
		const jobContext = buildJobContext(job, context, dealMap);

		// Standard (non-file) fields
		for (const fieldType of FIELD_TYPES_WITHOUT_FILES) {
			const value =
				fieldType === "salespersonMobilePhone"
					? job.newSalesperson?.phone // special case: nested in newSalesperson
					: (job as any)[FIELD_TO_PROPERTY_MAP[fieldType]];

			const fieldResult = validateField(fieldType, value, jobContext);
			if (!fieldResult.isValid) {
				issues.push({
					field: fieldType,
					message: fieldResult.error!,
					// An empty required value is "missing"; a present-but-malformed
					// value is "invalid". Derived from the value, not the message
					// string, so it stays correct even if a field omits a `required`
					// message and validateField uses its fallback string.
					kind: isEmpty(value, fieldType) ? "missing" : "invalid",
					blocking: isFieldVisible(fieldType, jobContext),
				});
			}
		}

		// File fields (validateFile only ever reports a missing required file).
		for (const fileType of FileTypes) {
			const fileResult = validateFile(fileType, job, jobContext);
			if (!fileResult.isValid) {
				const field = fileType as ImportTableColumn;
				issues.push({
					field,
					message: fileResult.error!,
					kind: "missing",
					blocking: isFieldVisible(field, jobContext),
				});
			}
		}

		issuesByJob[job.id] = issues;
	}

	return issuesByJob;
}

/**
 * Validate multiple jobs at once.
 * Returns an object with errors keyed by `${fieldType}-${jobId}` — ALL errors,
 * blocking and non-blocking. Derived from `validateJobsToIssues`; the external
 * contract is unchanged. Used by ImportTable.tsx and for per-field display.
 */
export function validateJobs(jobs: Job[], context: ValidationContext): Record<string, string> {
	const issuesByJob = validateJobsToIssues(jobs, context);
	const errors: Record<string, string> = {};
	for (const [jobId, issues] of Object.entries(issuesByJob)) {
		for (const issue of issues) {
			errors[getValidationErrorKey(issue.field, jobId)] = issue.message;
		}
	}
	return errors;
}

// #endregion
